// Asset register export — in-browser.
//
// Fetches properties + assets directly from Supabase and builds CSV/XLSX
// in the browser.

import { supabase } from "@/shared/integrations/supabase/client";
import { fetchClientProperties } from "@/features/projects/services/projectsApi";
import * as XLSX from "xlsx-js-style";
import { csvCell, sanitize, triggerDownload } from "./_helpers";
import { downloadWorkbook } from "./_xlsx";
import type { ExportFormat } from "./types";

type AssetRegisterExportFormat = ExportFormat | "json";

interface ExportAssetsInput {
	projectId: string;
	/** Filename prefix shown to the user. Typically the project or property name. */
	label: string;
	/** Filter to a single property (not a state filter — that's `state` below). */
	propertyId?: string;
	/** Comma-separated category names; partial case-insensitive match. */
	category?: string;
	/** Partial case-insensitive match against properties.state. */
	state?: string;
	/** Partial case-insensitive match against property_assets.manufacturer. */
	manufacturer?: string;
	format: AssetRegisterExportFormat;
}

interface AssetRow {
	id: string;
	property_id: string;
	name: string | null;
	description: string | null;
	category_id: string | null;
	asset_categories: { name: string } | null;
	manufacturer: string | null;
	model_name: string | null;
	model_number: string | null;
	serial_number: string | null;
	manufacture_date: string | null;
	estimated_age_years: number | null;
	condition_status: string | null;
	category_attributes: Record<string, unknown> | null;
	notes: string | null;
	zone_id: string | null;
	project_zones: { name: string } | null;
	nameplate_photo_url: string | null;
	asset_photo_url: string | null;
	qr_code_id: string | null;
}

interface PropertyLite {
	id: string;
	name: string | null;
	address: string | null;
	city: string | null;
	state: string | null;
}

const PROPERTY_BATCH = 40;
const PAGE_SIZE = 1000;

export async function exportAssets(input: ExportAssetsInput): Promise<void> {
	const properties = await fetchProperties(
		input.projectId,
		input.propertyId,
		input.state
	);
	if (properties.length === 0) {
		throw new Error("No properties match the requested filters");
	}
	const propertyMap = new Map(properties.map((p) => [p.id, p]));
	const propertyIds = properties.map((p) => p.id);

	const assets = await fetchAssets(propertyIds, {
		category: input.category,
		manufacturer: input.manufacturer,
	});

	const safeLabel = sanitize(input.label).toLowerCase();
	const filename = `${safeLabel}_assets.${input.format}`;
	const csvText = buildCsv(assets, propertyMap);

	if (input.format === "json") {
		triggerDownload(
			JSON.stringify(buildJson(assets, propertyMap), null, 2),
			filename,
			"application/json"
		);
		return;
	}

	if (input.format === "csv") {
		triggerDownload(csvText, filename, "text/csv;charset=utf-8");
		return;
	}

	// XLSX: parse the in-memory CSV with SheetJS, style the header, write out.
	const workbook = XLSX.read(csvText, { type: "string" });
	const sheetName = workbook.SheetNames[0] ?? "Assets";
	const sheet = workbook.Sheets[sheetName];

	const range = XLSX.utils.decode_range(sheet["!ref"] ?? "A1");
	for (let c = range.s.c; c <= range.e.c; c++) {
		const ref = XLSX.utils.encode_cell({ r: 0, c });
		if (!sheet[ref]) continue;
		sheet[ref].s = {
			font: { color: { rgb: "FFFFFF" }, bold: true },
			fill: { fgColor: { rgb: "000000" } },
			alignment: { wrapText: true, vertical: "top" },
		};
	}

	const out = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(out, sheet, "Assets");
	downloadWorkbook(out, filename);
}

async function fetchProperties(
	projectId: string,
	propertyId: string | undefined,
	state: string | undefined
): Promise<PropertyLite[]> {
	return fetchClientProperties<PropertyLite>(
		projectId,
		"id, name, address, city, state",
		(q) => {
			if (propertyId) q = q.eq("id", propertyId);
			if (state) q = q.ilike("state", `%${state}%`);
			return q;
		},
	);
}

async function fetchAssets(
	propertyIds: string[],
	filters: { category?: string; manufacturer?: string }
): Promise<AssetRow[]> {
	const out: AssetRow[] = [];
	const categories = filters.category
		? filters.category
				.split(",")
				.map((c) => c.trim())
				.filter(Boolean)
		: [];
	const normalizedCategories = categories.map((c) => c.toLowerCase());

	for (let i = 0; i < propertyIds.length; i += PROPERTY_BATCH) {
		const chunk = propertyIds.slice(i, i + PROPERTY_BATCH);

		let offset = 0;
		while (true) {
			let q = supabase
				.from("property_assets")
				.select(
					"id, property_id, name, description, category_id, asset_categories(name), manufacturer, " +
						"model_name, model_number, serial_number, manufacture_date:manufacture_date_string, estimated_age_years, " +
						"condition_status, category_attributes, notes, zone_id, project_zones(name), " +
						"nameplate_photo_url, asset_photo_url, qr_code_id"
				)
				.in("property_id", chunk)
				.order("property_id");

			if (filters.manufacturer) {
				q = q.ilike("manufacturer", `%${filters.manufacturer}%`);
			}

			const { data, error } = await q.range(offset, offset + PAGE_SIZE - 1);
			if (error) throw new Error(error.message || "Failed to fetch assets");
			if (!data || data.length === 0) break;

			const rows = data as unknown as AssetRow[];
			out.push(
				...(normalizedCategories.length > 0
					? rows.filter((asset) => {
							const categoryName =
								asset.asset_categories?.name?.toLowerCase() ?? "";
							return normalizedCategories.some((category) =>
								categoryName.includes(category)
							);
					  })
					: rows)
			);
			if (data.length < PAGE_SIZE) break;
			offset += PAGE_SIZE;
		}
	}

	return out;
}

/** Format manufacture_date to a readable date string. */
function formatDate(dateStr: string | null): string {
	if (!dateStr) return "";
	try {
		return new Date(dateStr).toLocaleDateString("en-US", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		});
	} catch {
		return dateStr;
	}
}

function stringifyCategoryAttributes(
	attrs: Record<string, unknown> | null
): string {
	if (!attrs || typeof attrs !== "object" || Object.keys(attrs).length === 0)
		return "";
	return JSON.stringify(attrs);
}

const BASE_HEADERS = [
	"Store",
	"Address",
	"City",
	"State",
	"Category",
	"Name",
	"Description",
	"Manufacturer",
	"Model Name",
	"Model Number",
	"Serial Number",
	"Manufacture Date",
	"Estimated Age (Years)",
	"Condition Status",
	"Location in Store",
	"Attributes",
	"Notes",
	"QR Code ID",
] as const;

function buildCsv(
	assets: AssetRow[],
	propertyMap: Map<string, PropertyLite>
): string {
	const headers = [...BASE_HEADERS];

	const rows = assets.map((a) => {
		const prop = propertyMap.get(a.property_id);
		const base = [
			csvCell(prop?.name),
			csvCell(prop?.address),
			csvCell(prop?.city),
			csvCell(prop?.state),
			csvCell(a.asset_categories?.name),
			csvCell(a.name),
			csvCell(a.description),
			csvCell(a.manufacturer),
			csvCell(a.model_name),
			csvCell(a.model_number),
			csvCell(a.serial_number),
			csvCell(formatDate(a.manufacture_date)),
			a.estimated_age_years?.toString() ?? "",
			csvCell(a.condition_status),
			csvCell(a.project_zones?.name),
			csvCell(stringifyCategoryAttributes(a.category_attributes)),
			csvCell(a.notes),
			csvCell(a.qr_code_id),
		];

		return [
			...base,
			csvCell(a.nameplate_photo_url),
			csvCell(a.asset_photo_url),
		].join(",");
	});

	return [
		[...headers, "Nameplate Photo", "Asset Photo"].join(","),
		...rows,
	].join("\n");
}

function buildJson(
	assets: AssetRow[],
	propertyMap: Map<string, PropertyLite>
): Array<Record<string, unknown>> {
	return assets.map((asset) => {
		const property = propertyMap.get(asset.property_id);
		return {
			property: property
				? {
						id: property.id,
						name: property.name,
						address: property.address,
						city: property.city,
						state: property.state,
				  }
				: null,
			asset,
		};
	});
}
